import { Dropdown } from "react-bootstrap";
import { Airplane, ArrowLeftRight, CheckCircleFill } from "react-bootstrap-icons";
import DropdownItem from "react-bootstrap/DropdownItem";
import { useTranslation } from "react-i18next";
import { hasAbsence } from "../../../helpers/absence";
import dayjs from "../../../helpers/dayjs";
import HourglassGlobals from "../../../helpers/globals";
import { userCompare } from "../../../helpers/user";
import { stringCompare } from "../../../helpers/util";
import { Permission } from "../../../types/permission";
import { Absence } from "../../../types/scheduling/absence";
import { PublicWitnessingLocation, PublicWitnessingSchedule } from "../../../types/scheduling/publicWitnessing";
import { Congregation } from "../../../types/scheduling/weekend";
import User from "../../../types/user";
import { DelegateUser } from "../../../types/whoami";

export enum PublicWitnessingTab {
  Schedule,
  Locations,
  Availability,
  Preferences,
  Statistics,
}

export const pwStoredDateKind = "pw";
export const smpwStoredDateKind = "smpw";

export function canUpdatePublicWitnessingSchedules(): boolean {
  return HourglassGlobals.permissions.has(Permission.UpdatePublicMinistrySchedules);
}

export function pwLocationCompare(a: PublicWitnessingLocation, b: PublicWitnessingLocation) {
  return stringCompare(a.name, b.name);
}

function scheduleKey(s: PublicWitnessingSchedule, location: string): string {
  return `${s.datetime}_${location}`;
}

// groupSchedules groups assignees with the same location and datetime into the same row
export function groupSchedules(
  schedules: PublicWitnessingSchedule[],
  locations: PublicWitnessingLocation[],
): Map<string, PublicWitnessingSchedule[]> {
  const locationMap = new Map<number, PublicWitnessingLocation>(locations.map((l) => [l.id, l]));
  const scheduledAssignments = new Map<string, PublicWitnessingSchedule[]>();
  schedules.forEach((s) => {
    const loc = locationMap.get(s.location_id)?.name || s.location_id.toString();
    const key = scheduleKey(s, loc);
    const current = scheduledAssignments.get(key);
    if (current) scheduledAssignments.set(key, [...current, s]);
    else scheduledAssignments.set(key, [s]);
  });

  return scheduledAssignments;
}

export function UserDropdownItems(props: {
  possible: User[];
  availableUserIds: Set<number>;
  absences: Absence[];
  schedule: PublicWitnessingSchedule;
  sharedUserMap: Map<number, DelegateUser>;
  addAssignee: (userId: number | null) => void;
  sharedCongs: Congregation[];
}) {
  const { t } = useTranslation();

  const absenceDjs = dayjs(props.schedule.datetime);

  const dropdownForUser = (u: User) => {
    const isAbsent =
      props.absences && absenceDjs.isValid() && hasAbsence(u.id, absenceDjs.toDate(), props.absences, true);
    const sharedUser = props.sharedUserMap.get(u.id);
    return (
      <DropdownItem key={`u_${props.schedule.id}_${u.id}`} disabled={isAbsent} onClick={() => props.addAssignee(u.id)}>
        <div className="d-flex justify-content-between">
          {u.displayName}
          {!!sharedUser && <ArrowLeftRight className="active-speaker-link" />}
          {isAbsent && <Airplane className="ms-2" />}
        </div>
        {/*for users in other congregations, show the congregation name below the user's name*/}
        {!!sharedUser && (
          <div className="text-muted hg-small-label ms-2">
            {props.sharedCongs.find((c) => c.id === sharedUser.congregationId)?.name}
          </div>
        )}
      </DropdownItem>
    );
  };

  return (
    <>
      {props.possible.some((u) => props.availableUserIds.has(u.id)) && (
        <>
          <Dropdown.Divider />
          <Dropdown.Header>
            {t("schedules.public-witnessing.availability")} <CheckCircleFill color="green" />
          </Dropdown.Header>
          {props.possible
            .filter((u) => props.availableUserIds.has(u.id))
            .sort(userCompare)
            .map((u) => dropdownForUser(u))}
        </>
      )}
      {/*if we have users not available, they're here*/}
      {props.possible.some((u) => !props.availableUserIds.has(u.id)) && (
        <>
          <Dropdown.Divider />
          {props.possible
            .filter((u) => !props.availableUserIds.has(u.id))
            .sort(userCompare)
            .map((u) => dropdownForUser(u))}
        </>
      )}
    </>
  );
}

export const blankSchedule: PublicWitnessingSchedule = {
  id: 0,
  location_id: 0,
  datetime: "",
  duration: "",
  assignee_id: 0,
};
